/**
 * VÉRAULT — Public Onboarding form (Forms Phase 1)
 *
 * Magic-link landing page at /onboarding/:token. Fully public — no portal
 * shell, no advisor chrome, no auth requirement. The token IS the credential;
 * an email-or-phone soft second factor unlocks the form on first open.
 *
 * Editorial luxury: oversized serif headings, deep negative space, a soft
 * column-and-rail layout that collapses gracefully on mobile. The point is
 * that this should feel like opening a private letter, not filing a tax
 * return.
 *
 * Flow:
 *   1. Pre-verify gate — enter the email or phone the advisor has on file
 *   2. Section rail (left, sticky on desktop) + active section (right)
 *   3. Per-section autosave on each keystroke (debounced)
 *   4. Explicit "Save & continue" on each section moves to the next
 *   5. Final "Submit packet" finalizes the bundle
 */
import React from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle2,
  Loader2,
  ChevronRight,
  ChevronLeft,
  Mail,
  Phone,
  ScrollText,
  Plus,
  Trash2,
  AlertCircle,
  Lock,
  Sparkles,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/forms`;

// Lightweight markdown renderer for the embedded documents. Editorial typography
// is more important than exhaustive markdown — we handle headings, bold, italic,
// hr, bullet lists, and paragraph breaks. Everything else passes through.
function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const blocks = [];
  let listBuffer = [];
  const flushList = () => {
    if (listBuffer.length > 0) {
      blocks.push({ type: "ul", items: listBuffer.slice() });
      listBuffer = [];
    }
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushList(); blocks.push({ type: "spacer" }); continue; }
    if (line.startsWith("# ")) { flushList(); blocks.push({ type: "h1", text: line.slice(2) }); continue; }
    if (line.startsWith("## ")) { flushList(); blocks.push({ type: "h2", text: line.slice(3) }); continue; }
    if (line.startsWith("- ") || line.startsWith("* ")) { listBuffer.push(line.slice(2)); continue; }
    if (/^[-*_]{3,}$/.test(line)) { flushList(); blocks.push({ type: "hr" }); continue; }
    flushList();
    blocks.push({ type: "p", text: line });
  }
  flushList();
  const inline = (s) =>
    s
      .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
      .replace(/\*([^*]+)\*/g, "<em>$1</em>")
      .replace(/_([^_]+)_/g, "<em>$1</em>");

  return blocks.map((b, i) => {
    if (b.type === "h1") return <h2 key={i} className="font-serif text-2xl mt-6 mb-3" dangerouslySetInnerHTML={{ __html: inline(b.text) }} />;
    if (b.type === "h2") return <h3 key={i} className="font-serif text-lg mt-5 mb-2" dangerouslySetInnerHTML={{ __html: inline(b.text) }} />;
    if (b.type === "ul") return (
      <ul key={i} className="my-3 space-y-1.5 pl-5 list-disc marker:text-[hsl(var(--accent-gold,38_55%_45%))]">
        {b.items.map((item, j) => (
          <li key={j} className="font-body text-[14.5px] leading-relaxed" dangerouslySetInnerHTML={{ __html: inline(item) }} />
        ))}
      </ul>
    );
    if (b.type === "hr") return <hr key={i} className="my-6 border-[hsl(var(--accent-gold,38_55%_45%))]/30" />;
    if (b.type === "p") return <p key={i} className="font-body text-[14.5px] leading-relaxed my-3" dangerouslySetInnerHTML={{ __html: inline(b.text) }} />;
    if (b.type === "spacer") return <div key={i} className="h-2" />;
    return null;
  });
}

// ============================================================================
// Verification gate
// ============================================================================

function VerificationGate({ token, packet, onVerified }) {
  const [mode, setMode] = React.useState(
    packet.verification_channels.email ? "email" : "phone",
  );
  const [value, setValue] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const handleVerify = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    setError(null);
    try {
      const payload = mode === "email" ? { email: value } : { phone: value };
      await axios.post(`${API}/p/${token}/verify`, payload);
      onVerified();
    } catch (e) {
      setError(e?.response?.data?.detail || "That doesn't match what's on file.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background,40_30%_98%))] flex items-center justify-center px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-lg"
      >
        <div className="text-center mb-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--accent-gold,38_55%_45%))]">
            VÉRAULT TRAVELS
          </p>
          <h1 className="font-serif text-4xl md:text-5xl mt-3 leading-tight">
            Welcome{packet.client_first_name_hint ? `, ${packet.client_first_name_hint}` : ""}.
          </h1>
          <p className="font-accent italic text-[15px] text-[hsl(var(--foreground-secondary,210_8%_30%))] mt-3 max-w-md mx-auto leading-relaxed">
            Before we open the rest of the form, a quick verification — just to confirm this link reached the right person.
          </p>
        </div>

        <form onSubmit={handleVerify} className="space-y-5">
          {packet.verification_channels.email && packet.verification_channels.phone && (
            <div className="flex items-center gap-2" data-testid="onboarding-verify-channel-picker">
              {[
                { id: "email", label: "Email", icon: Mail },
                { id: "phone", label: "Phone", icon: Phone },
              ].map((c) => {
                const active = mode === c.id;
                const Icon = c.icon;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => { setMode(c.id); setValue(""); }}
                    data-testid={`onboarding-verify-channel-${c.id}`}
                    className={`flex-1 px-4 py-2.5 rounded-md inline-flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-all ${
                      active
                        ? "bg-[hsl(var(--foreground,210_15%_15%))] text-white"
                        : "bg-white text-[hsl(var(--foreground-muted,210_8%_45%))] hover:bg-[hsl(var(--background-paper,40_30%_94%))]"
                    }`}
                  >
                    <Icon className="size-3" strokeWidth={1.5} />
                    {c.label}
                  </button>
                );
              })}
            </div>
          )}

          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted,210_8%_45%))] mb-2 block">
              {mode === "email" ? "Email on file" : "Phone on file"}
            </label>
            <input
              type={mode === "email" ? "email" : "tel"}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={mode === "email" ? "you@example.com" : "+1 555 0100"}
              className="w-full px-4 py-3 text-[15px] bg-white border border-[rgba(0,0,0,0.08)] rounded-md outline-none focus:border-[hsl(var(--accent-gold,38_55%_45%))] transition-colors"
              data-testid="onboarding-verify-input"
              autoFocus
              required
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 px-3 py-2.5 rounded-md bg-[#FCEAE0] border border-[#F0C8A8]" data-testid="onboarding-verify-error">
              <AlertCircle className="size-4 text-[#8B4513] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
              <p className="font-body text-[13px] text-[#8B4513]">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={busy || !value.trim()}
            data-testid="onboarding-verify-submit"
            className="w-full px-6 py-3.5 rounded-md bg-[hsl(var(--accent-gold,38_55%_45%))] text-white font-mono text-[11px] uppercase tracking-[0.22em] hover:opacity-90 transition-opacity disabled:opacity-40 inline-flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Lock className="size-3.5" strokeWidth={1.5} />}
            {busy ? "Verifying" : "Continue"}
          </button>
        </form>

        <p className="mt-8 font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted,210_8%_45%))] text-center leading-relaxed">
          Your information is held privately by your advisor. Nothing is shared until you submit each section.
        </p>
      </motion.div>
    </div>
  );
}

// ============================================================================
// Section renderers
// ============================================================================

function FieldText({ field, value, onChange }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted,210_8%_45%))] mb-2 block">
        {field.label}{field.required && " *"}
      </label>
      <input
        type={field.type === "email" ? "email" : field.type === "phone" ? "tel" : field.type === "date" ? "date" : field.type === "number" ? "number" : "text"}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder || ""}
        className="w-full px-4 py-3 text-[15px] bg-white border border-[rgba(0,0,0,0.08)] rounded-md outline-none focus:border-[hsl(var(--accent-gold,38_55%_45%))] transition-colors"
        data-testid={`onboarding-field-${field.key}`}
      />
    </div>
  );
}

function FieldTextarea({ field, value, onChange }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted,210_8%_45%))] mb-2 block">
        {field.label}{field.required && " *"}
      </label>
      <textarea
        rows={4}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 text-[15px] bg-white border border-[rgba(0,0,0,0.08)] rounded-md outline-none focus:border-[hsl(var(--accent-gold,38_55%_45%))] transition-colors resize-none"
        data-testid={`onboarding-field-${field.key}`}
      />
    </div>
  );
}

function FieldSelect({ field, value, onChange }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted,210_8%_45%))] mb-2 block">
        {field.label}{field.required && " *"}
      </label>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 text-[15px] bg-white border border-[rgba(0,0,0,0.08)] rounded-md outline-none focus:border-[hsl(var(--accent-gold,38_55%_45%))] transition-colors cursor-pointer"
        data-testid={`onboarding-field-${field.key}`}
      >
        <option value="">Select…</option>
        {(field.options || []).map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );
}

function FieldMultiselect({ field, value, onChange }) {
  const arr = Array.isArray(value) ? value : [];
  const toggle = (opt) => {
    if (arr.includes(opt)) onChange(arr.filter((x) => x !== opt));
    else onChange([...arr, opt]);
  };
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted,210_8%_45%))] mb-2 block">
        {field.label}{field.required && " *"}
      </label>
      <div className="grid sm:grid-cols-2 gap-2" data-testid={`onboarding-field-${field.key}`}>
        {(field.options || []).map((opt) => {
          const checked = arr.includes(opt);
          return (
            <label
              key={opt}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-md cursor-pointer transition-colors border ${
                checked
                  ? "bg-[hsl(var(--accent-gold,38_55%_45%))]/10 border-[hsl(var(--accent-gold,38_55%_45%))]/30"
                  : "bg-white border-[rgba(0,0,0,0.06)] hover:border-[rgba(0,0,0,0.12)]"
              }`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(opt)}
                className="accent-[hsl(var(--accent-gold,38_55%_45%))]"
              />
              <span className="font-body text-[13.5px]">{opt}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function FieldCheckbox({ field, value, onChange }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 accent-[hsl(var(--accent-gold,38_55%_45%))]"
        data-testid={`onboarding-field-${field.key}`}
      />
      <span className="font-body text-[14px] leading-relaxed">
        {field.label}{field.required && " *"}
      </span>
    </label>
  );
}

function FieldDocument({ field, documents }) {
  const doc = documents[field.document_slug];
  if (!doc) {
    return <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted,210_8%_45%))]">Document unavailable.</p>;
  }
  return (
    <div className="border border-[rgba(0,0,0,0.08)] rounded-md bg-white" data-testid={`onboarding-doc-${field.document_slug}`}>
      <div className="px-5 py-3 border-b border-[rgba(0,0,0,0.05)] flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted,210_8%_45%))]">
          {doc.title}
        </p>
        <span className="font-mono text-[10px] text-[hsl(var(--foreground-muted,210_8%_45%))]">v{doc.version}</span>
      </div>
      <div className="px-5 py-4 max-h-72 overflow-y-auto">
        {renderMarkdown(doc.content)}
      </div>
    </div>
  );
}

function FieldTravelerList({ field, value, onChange, parentTravelers }) {
  // If `from_section: "traveler_details"` is set, render one entry per traveler
  // from the parent submission. Otherwise, render an empty-start add/remove list.
  const fromOther = field.from_section && Array.isArray(parentTravelers);
  const arr = Array.isArray(value) ? value : [];
  // Synced rows used only when fromOther=true; computed unconditionally so the
  // useEffect below stays at top level (Rules of Hooks).
  const synced = React.useMemo(() => {
    if (!fromOther) return [];
    return parentTravelers.map((t, i) => {
      const existing = arr[i] || {};
      return { ...existing, legal_full_name: t.legal_full_name || existing.legal_full_name || "" };
    });
  }, [fromOther, parentTravelers, arr]);
  // Push the synced shape up to the parent whenever traveler count changes
  const parentCount = fromOther ? parentTravelers.length : -1;
  React.useEffect(() => {
    if (fromOther) onChange(synced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentCount, fromOther]);

  if (fromOther) {
    const updateOne = (i, patch) => {
      const next = synced.map((row, idx) => (idx === i ? { ...row, ...patch } : row));
      onChange(next);
    };
    if (parentTravelers.length === 0) {
      return (
        <p className="font-accent italic text-[13.5px] text-[hsl(var(--foreground-muted,210_8%_45%))] leading-relaxed">
          Once you've added travelers in the previous section, their passport entries will appear here.
        </p>
      );
    }
    return (
      <div className="space-y-5" data-testid={`onboarding-field-${field.key}`}>
        {synced.map((row, i) => (
          <div key={i} className="border border-[rgba(0,0,0,0.06)] rounded-md p-5 bg-white space-y-3">
            <p className="font-serif text-base">{row.legal_full_name || `Traveler ${i + 1}`}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {(field.fields || []).map((sub) => (
                <div key={sub.key} className={sub.type === "textarea" ? "sm:col-span-2" : ""}>
                  {sub.type === "textarea" ? (
                    <FieldTextarea field={sub} value={row[sub.key]} onChange={(v) => updateOne(i, { [sub.key]: v })} />
                  ) : (
                    <FieldText field={sub} value={row[sub.key]} onChange={(v) => updateOne(i, { [sub.key]: v })} />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Free-form traveler list (add / remove rows)
  const updateOne = (i, patch) => {
    const next = arr.map((row, idx) => (idx === i ? { ...row, ...patch } : row));
    onChange(next);
  };
  const addRow = () => onChange([...arr, {}]);
  const removeRow = (i) => onChange(arr.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-4" data-testid={`onboarding-field-${field.key}`}>
      {arr.map((row, i) => (
        <div key={i} className="border border-[rgba(0,0,0,0.06)] rounded-md p-5 bg-white space-y-3 relative">
          <div className="flex items-center justify-between gap-2">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted,210_8%_45%))]">
              Traveler {i + 1}
            </p>
            <button
              type="button"
              onClick={() => removeRow(i)}
              className="text-[hsl(var(--foreground-muted,210_8%_45%))] hover:text-[hsl(var(--accent-terracotta,15_60%_55%))]"
              data-testid={`onboarding-traveler-remove-${i}`}
              aria-label="Remove traveler"
            >
              <Trash2 className="size-3.5" strokeWidth={1.5} />
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {(field.fields || []).map((sub) => (
              <FieldText key={sub.key} field={sub} value={row[sub.key]} onChange={(v) => updateOne(i, { [sub.key]: v })} />
            ))}
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={addRow}
        className="w-full px-4 py-3 rounded-md border border-dashed border-[rgba(0,0,0,0.15)] text-[hsl(var(--foreground-muted,210_8%_45%))] hover:border-[hsl(var(--accent-gold,38_55%_45%))] hover:text-[hsl(var(--accent-gold,38_55%_45%))] transition-colors inline-flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em]"
        data-testid="onboarding-traveler-add"
      >
        <Plus className="size-3.5" strokeWidth={1.5} />
        Add a traveler
      </button>
    </div>
  );
}

function Field({ field, value, onChange, documents, parentTravelers }) {
  if (field.type === "document") return <FieldDocument field={field} documents={documents} />;
  if (field.type === "textarea") return <FieldTextarea field={field} value={value} onChange={onChange} />;
  if (field.type === "select") return <FieldSelect field={field} value={value} onChange={onChange} />;
  if (field.type === "multiselect") return <FieldMultiselect field={field} value={value} onChange={onChange} />;
  if (field.type === "checkbox") return <FieldCheckbox field={field} value={value} onChange={onChange} />;
  if (field.type === "traveler_list") return <FieldTravelerList field={field} value={value} onChange={onChange} parentTravelers={parentTravelers} />;
  return <FieldText field={field} value={value} onChange={onChange} />;
}

// ============================================================================
// Section view
// ============================================================================

function SectionView({ section, value, onChange, onSave, onNext, onPrev, hasPrev, hasNext, isLast, saving, busySaving, documents, parentTravelers, completed }) {
  const update = (key, v) => onChange({ ...value, [key]: v });
  const handleSubmit = (e) => {
    e?.preventDefault?.();
    onSave({ next: true });
  };

  return (
    <motion.form
      key={section.slug}
      initial={{ opacity: 0, x: 12 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -12 }}
      transition={{ duration: 0.4 }}
      onSubmit={handleSubmit}
      className="space-y-6"
      data-testid={`onboarding-section-${section.slug}`}
    >
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--accent-gold,38_55%_45%))]">
          Section
        </p>
        <h2 className="font-serif text-3xl md:text-4xl mt-2 leading-tight">{section.name}</h2>
        <p className="font-accent italic text-[15px] text-[hsl(var(--foreground-secondary,210_8%_30%))] mt-3 max-w-xl leading-relaxed">
          {section.description}
        </p>
      </div>

      <div className="space-y-5">
        {(section.fields || []).map((field) => (
          <Field
            key={field.key}
            field={field}
            value={value?.[field.key]}
            onChange={(v) => update(field.key, v)}
            documents={documents}
            parentTravelers={parentTravelers}
          />
        ))}
        {section.requires_signature && (
          <div data-testid="onboarding-signature">
            <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted,210_8%_45%))] mb-2 block">
              Signature — your full legal name, typed
            </label>
            <input
              type="text"
              value={value?.signer_name || ""}
              onChange={(e) => update("signer_name", e.target.value)}
              placeholder="Type your full name to sign"
              className="w-full px-4 py-3 text-[18px] font-serif italic bg-white border border-[rgba(0,0,0,0.08)] rounded-md outline-none focus:border-[hsl(var(--accent-gold,38_55%_45%))] transition-colors"
              data-testid="onboarding-signer-name"
            />
            <p className="mt-2 font-accent italic text-[12px] text-[hsl(var(--foreground-muted,210_8%_45%))]">
              By typing your name above and submitting, you provide an electronic signature equivalent in effect to a handwritten one.
            </p>
          </div>
        )}
      </div>

      <div className="pt-4 border-t border-[rgba(0,0,0,0.06)] flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={onPrev}
          disabled={!hasPrev || busySaving}
          className="px-4 py-2.5 rounded-md inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted,210_8%_45%))] hover:text-[hsl(var(--foreground,210_15%_15%))] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid="onboarding-prev"
        >
          <ChevronLeft className="size-3.5" strokeWidth={1.5} />
          Previous
        </button>
        <div className="flex items-center gap-2">
          {saving && (
            <span className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted,210_8%_45%))]" data-testid="onboarding-autosaving">
              Saving…
            </span>
          )}
          {completed && (
            <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-sage,170_18%_42%))]" data-testid={`onboarding-section-completed-${section.slug}`}>
              <CheckCircle2 className="size-3" strokeWidth={1.6} />
              Saved
            </span>
          )}
          <button
            type="submit"
            disabled={busySaving}
            data-testid="onboarding-save-next"
            className="px-6 py-3 rounded-md bg-[hsl(var(--foreground,210_15%_15%))] text-white font-mono text-[11px] uppercase tracking-[0.22em] hover:opacity-90 transition-opacity inline-flex items-center gap-2 disabled:opacity-40"
          >
            {busySaving ? <Loader2 className="size-3.5 animate-spin" /> : isLast ? <Sparkles className="size-3.5" strokeWidth={1.5} /> : <ChevronRight className="size-3.5" strokeWidth={1.5} />}
            {busySaving ? "Saving" : isLast ? "Submit" : "Save & continue"}
          </button>
        </div>
      </div>
    </motion.form>
  );
}

// ============================================================================
// Completion screen
// ============================================================================

function CompletionScreen({ packet }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7 }}
      className="min-h-screen flex items-center justify-center px-6 py-12 bg-[hsl(var(--background,40_30%_98%))]"
      data-testid="onboarding-complete"
    >
      <div className="max-w-lg text-center">
        <div className="inline-flex items-center justify-center size-16 rounded-full bg-[hsl(var(--accent-gold,38_55%_45%))]/15 mb-6">
          <CheckCircle2 className="size-7 text-[hsl(var(--accent-gold,38_55%_45%))]" strokeWidth={1.5} />
        </div>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--accent-gold,38_55%_45%))]">
          Onboarding complete
        </p>
        <h1 className="font-serif text-4xl md:text-5xl mt-3 leading-tight">Thank you.</h1>
        <p className="font-accent italic text-[16px] text-[hsl(var(--foreground-secondary,210_8%_30%))] mt-4 leading-relaxed">
          Your advisor has been notified. You'll hear from them shortly with next steps. You can safely close this page.
        </p>
      </div>
    </motion.div>
  );
}

// ============================================================================
// Top-level page
// ============================================================================

export default function OnboardingForm() {
  const { token } = useParams();
  const [packet, setPacket] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [activeSlug, setActiveSlug] = React.useState(null);
  const [formData, setFormData] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [busySaving, setBusySaving] = React.useState(false);
  const saveTimerRef = React.useRef(null);

  const refresh = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/p/${token}`);
      setPacket(data);
      if (data.verified_at && data.sections?.length) {
        // Hydrate formData from drafts
        const hydrated = {};
        Object.entries(data.drafts || {}).forEach(([slug, sub]) => {
          hydrated[slug] = sub.data || {};
        });
        setFormData(hydrated);
        // Pick the first non-completed section, or the last one if all done
        const firstPending = data.sections.find((s) => !data.completed_section_slugs.includes(s.slug));
        setActiveSlug(firstPending?.slug || data.sections[0].slug);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Unable to load this onboarding link.");
    }
  }, [token]);

  React.useEffect(() => { refresh(); }, [refresh]);

  // Autosave drafts on every change (debounced 800ms)
  const triggerAutosave = React.useCallback((slug, data) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        await axios.patch(`${API}/p/${token}/draft/${slug}`, { data });
      } catch {
        // Silent — autosave is best-effort; explicit save catches the error
      } finally {
        setSaving(false);
      }
    }, 800);
  }, [token]);

  const handleFieldChange = (slug, next) => {
    setFormData((prev) => ({ ...prev, [slug]: next }));
    triggerAutosave(slug, next);
  };

  const handleSaveAndNext = async (slug, { next }) => {
    setBusySaving(true);
    try {
      const data = formData[slug] || {};
      const payload = { data };
      // Surface signer_name as a top-level field for the agreement section
      const sectionDef = packet.sections.find((s) => s.slug === slug);
      if (sectionDef?.requires_signature && data.signer_name) {
        payload.signer_name = data.signer_name;
      }
      const { data: result } = await axios.post(`${API}/p/${token}/submit/${slug}`, payload);
      // Refresh to pick up new completed_section_slugs + status
      await refresh();
      if (next && result.status !== "completed") {
        const idx = packet.sections.findIndex((s) => s.slug === slug);
        const nextSection = packet.sections[idx + 1];
        if (nextSection) setActiveSlug(nextSection.slug);
      }
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Couldn't save this section. Please try again.");
    } finally {
      setBusySaving(false);
    }
  };

  // ---- Error & loading states --------------------------------------
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-[hsl(var(--background,40_30%_98%))]">
        <div className="max-w-md text-center" data-testid="onboarding-error">
          <ScrollText className="size-7 text-[hsl(var(--foreground-muted,210_8%_45%))] mx-auto mb-3" strokeWidth={1.5} />
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--accent-gold,38_55%_45%))]">
            VÉRAULT
          </p>
          <h1 className="font-serif text-2xl md:text-3xl mt-2">This link isn't available.</h1>
          <p className="font-accent italic text-[14px] text-[hsl(var(--foreground-muted,210_8%_45%))] mt-3 leading-relaxed">
            {error}
          </p>
        </div>
      </div>
    );
  }
  if (!packet) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="onboarding-loading">
        <Loader2 className="size-6 animate-spin text-[hsl(var(--accent-gold,38_55%_45%))]" strokeWidth={1.5} />
      </div>
    );
  }

  // ---- Verification gate -------------------------------------------
  if (!packet.verified_at) {
    return <VerificationGate token={token} packet={packet} onVerified={refresh} />;
  }

  // ---- Completed --------------------------------------------------
  if (packet.status === "completed") {
    return <CompletionScreen packet={packet} />;
  }

  // ---- Active section -----------------------------------------------
  const sections = packet.sections || [];
  const activeIdx = Math.max(0, sections.findIndex((s) => s.slug === activeSlug));
  const activeSection = sections[activeIdx];
  const completedSlugs = new Set(packet.completed_section_slugs || []);
  const parentTravelers = (formData["traveler_details"]?.travelers) || (packet.drafts?.traveler_details?.data?.travelers) || [];
  const isLast = activeIdx === sections.length - 1;

  return (
    <div className="min-h-screen bg-[hsl(var(--background,40_30%_98%))]">
      {/* Header rail */}
      <header className="px-6 md:px-12 py-6 border-b border-[rgba(0,0,0,0.05)]" data-testid="onboarding-header">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--accent-gold,38_55%_45%))]">
          VÉRAULT TRAVELS · Onboarding
        </p>
      </header>

      <div className="px-6 md:px-12 py-10 md:py-14 max-w-6xl mx-auto grid lg:grid-cols-[260px_1fr] gap-10 lg:gap-16">
        {/* Section rail */}
        <aside className="lg:sticky lg:top-10 lg:self-start" data-testid="onboarding-rail">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted,210_8%_45%))] mb-4">
            Sections · {completedSlugs.size} of {sections.length} saved
          </p>
          <ol className="space-y-1.5">
            {sections.map((s, i) => {
              const active = s.slug === activeSlug;
              const done = completedSlugs.has(s.slug);
              return (
                <li key={s.slug}>
                  <button
                    type="button"
                    onClick={() => setActiveSlug(s.slug)}
                    data-testid={`onboarding-rail-${s.slug}`}
                    className={`w-full text-left px-3 py-2.5 rounded-md flex items-start gap-2.5 transition-colors ${
                      active
                        ? "bg-white shadow-sm border border-[hsl(var(--accent-gold,38_55%_45%))]/30"
                        : "hover:bg-white/60"
                    }`}
                  >
                    {done ? (
                      <CheckCircle2 className="size-3.5 mt-0.5 text-[hsl(var(--accent-sage,170_18%_42%))] flex-shrink-0" strokeWidth={1.6} />
                    ) : (
                      <span className={`mt-0.5 inline-flex items-center justify-center size-4 rounded-full text-[10px] font-mono flex-shrink-0 ${
                        active ? "bg-[hsl(var(--accent-gold,38_55%_45%))] text-white" : "bg-[hsl(var(--background-paper,40_30%_94%))] text-[hsl(var(--foreground-muted,210_8%_45%))]"
                      }`}>{i + 1}</span>
                    )}
                    <span className={`font-body text-[13.5px] leading-snug ${active ? "" : "text-[hsl(var(--foreground-secondary,210_8%_30%))]"}`}>
                      {s.name}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </aside>

        {/* Active section */}
        <main className="min-w-0">
          <AnimatePresence mode="wait">
            {activeSection && (
              <SectionView
                section={activeSection}
                value={formData[activeSection.slug] || {}}
                onChange={(next) => handleFieldChange(activeSection.slug, next)}
                onSave={(opts) => handleSaveAndNext(activeSection.slug, opts)}
                onNext={() => {
                  const next = sections[activeIdx + 1];
                  if (next) setActiveSlug(next.slug);
                }}
                onPrev={() => {
                  const prev = sections[activeIdx - 1];
                  if (prev) setActiveSlug(prev.slug);
                }}
                hasPrev={activeIdx > 0}
                hasNext={activeIdx < sections.length - 1}
                isLast={isLast}
                saving={saving}
                busySaving={busySaving}
                documents={packet.documents || {}}
                parentTravelers={parentTravelers}
                completed={completedSlugs.has(activeSection.slug)}
              />
            )}
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}
